import { ConfigStateService } from '@medlogic/shared/state-config';
import { EnActivityType, EnMessageSeverity, IMessage } from '@medlogic/shared/gecore';
import { UnsubscribeOnDestroyAdapter, IAtividadeComponenteDAL, ConfigJsonService, MsgPtBR, GlobalService, LogService, EnTheme } from '@medlogic/shared/shared-interfaces';
import { NavigationService } from '../../../shared/service/navigation.service';
import { LibService } from '../../../shared/service/lib.service';
import { FormGroup } from '@angular/forms';
import { IBubble } from '../../../shared/interface/ibubble';
import { CalculatorService } from '../../../shared/service/calculator.service';
import { DadoDAL } from '../../../shared/model/dal/dado-dal';
import { Component, OnInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { EnBubbleEvent } from '../../../shared/enum/en-bubble-event.enum';
import { IAtividade } from '../../../shared/interface/iatividade';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogConfirmComponent } from '@medlogic/shared/ui/dialog/ui-dialog-confirm';

/** AtividadeNo dos cadastros/atividades que não permitem duplicação de ocorrências. */
const ATIVIDADES_SEM_DUPLICACAO = new Set<number>([
  24421, // Admissão
  24439, // IVCF-20
  24659, // Cadastro de Profissionais
  24473, // Prospect IVCF-20
  24657, // Pré Admissão Cadastro de Idoso
  24649, // Pré Admissão Serviço Social
  24656, // Pré Admissão Enfermagem
  24658, // Pré Admissão Médica
  24666, // Atividades Individuais
  24679, // Atividades do Dia
  24681, // Saídas
  24685, // Internações
  24686, // Contratos
  24691, // Datas Comemorativas
  24692, // Tabela de Valores
  24725, // Escala Peso
  24731, // Cadastro sem duplicação
]);

// Barra de botões da Atividade.
@Component({
  selector: 'lib-action-bar',
  templateUrl: './action-bar.component.html',
  styleUrls: ['./action-bar.component.css']
})
export class ActionBarComponent extends UnsubscribeOnDestroyAdapter implements OnInit, OnChanges {

  @Input() componentes: IAtividadeComponenteDAL[];
  @Input() enTheme = EnTheme.default;
  @Input() tarefaNo: number;
  @Input() formErrors: any[];
  @Input() msgs: IMessage[] = []; // Preencha essa variável para que seja exibido uma div no rodapé.
  @Input() activityType: EnActivityType;
  @Input() saveInList: boolean;
  @Input() addToHistory = true;
  @Input() formGroup: FormGroup;
  @Input() isMobile: boolean;
  @Input() isLoading = false;
  @Input() isEdit = false
  @Input() canShowSavedMessages: boolean;
  /** Habilita o botão Duplicar (ativado quando a ocorrência foi aberta a partir do generic-list padrão). */
  @Input() canDuplicate = false;
  /** Modo de duplicação ativa: esconde Duplicar e SaveAndNew, mantendo apenas a ação primária de salvar. */
  @Input() isDuplicateMode = false;
  /** VariavelNo dos campos cujo valor deve ser sobrescrito com a data atual ao duplicar uma ocorrência. */
  @Input() variaveisDataReset: number[] = [];
  /** Quando true, exibe popup de confirmação antes de duplicar a ocorrência (ex.: Evolução). */
  @Input() showDuplicateConfirmation = false;
  @Input() set atividade(v: IAtividade) {
    this._atividade = v;
    if (v) {
      this.hasConfirm = this.hasConfirmButton(this.activityType);
    }
  }

  @Input() public set saved(v: boolean) {
    this._saved = v;
    if (v) {
      this.showSaveMessage(true);
    }
  }

  @Output() eventBubble: EventEmitter<IBubble> = new EventEmitter<IBubble>();

  attachmentCount = 1;

  public _atividade: IAtividade;
  get atividade(): IAtividade {
    return this._atividade;
  }

  public disable: boolean = false

  private _hasConfirm = false;
  public get hasConfirm(): boolean {
    return (this.atividade.ShowComplete || this.isListDetail()) && !this.config.isReadOnly;
  }
  public set hasConfirm(v: boolean) {
    this._hasConfirm = v;
  }

  private _saved: boolean;
  public get saved(): boolean {
    return this._saved;
  }

  public get textConfirm(): string {
    return this.msg.BUTTON_CONFIRM;
  }

  public get hasSave(): boolean {
    return this.atividade.ShowSave && !this.isListDetail() && !this.config.isReadOnly;
  }

  public get textSave(): string {
    return this.atividade.TextSave;
  }

  public get hasSaveAndNew(): boolean {
    if (this.cnfJson.showOnlySaveButton || this.isDuplicateMode) {
      return false;
    }
    return this.atividade.ShowSaveAndNew && !this.isListDetail() && !this.config.isReadOnly;
  }

  public get textSaveAndNew(): string {
    return this.atividade.TextSaveAndNew || this.msg.BTN_SAVE_AND_NEW;
  }

  /** Retorna true somente para processos que permitem duplicação de ocorrências. */
  protected isDuplicateAllowedForProcess(): boolean {
    return !ATIVIDADES_SEM_DUPLICACAO.has(this.atividade?.AtividadeNo);
  }

  public get hasDuplicate(): boolean {
    if (this.cnfJson.showOnlySaveButton || this.isDuplicateMode) {
      return false;
    }
    // Cadastros configurados em ATIVIDADES_SEM_DUPLICACAO não permitem duplicação
    if (!this.isDuplicateAllowedForProcess()) {
      return false;
    }
    // Habilitado via input (fluxo generic-list)
    if (this.canDuplicate) {
      return !this.config.isReadOnly;
    }
    // Legado: controlado pela configuração da atividade
    if (this.atividade.ShowDuplicate != null) {
      return this.atividade.ShowDuplicate;
    }
    return false;
  }

  public get textDuplicate(): string {
    return this.msg.BTN_DUPLICATE;
  }

  onEventBubble($event: IBubble) {
    this.eventBubble.emit($event);
  }

  constructor(
    public config: ConfigStateService,
    public cnfJson: ConfigJsonService,
    protected log: LogService,
    protected dadoDAL: DadoDAL,
    protected global: GlobalService,
    protected calc: CalculatorService,
    protected lib: LibService,
    protected nav: NavigationService,
    protected matDialog: MatDialog,
    protected msg: MsgPtBR,
    protected snackBar: MatSnackBar
  ) {
    super();
  }

  ngOnInit() {
    try {
      this.hasConfirm = this.atividade && this.atividade.ShowSave && this.isListDetail();
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'ngOnInit', error.message);
    }
  }

  changeDisable() {
    this.disable = !this.disable
  }

  clicked() {
    this.changeDisable()
  }

  /* Checa se o botão de confirmação deve ou não aparecer. */
  protected hasConfirmButton(activityType: EnActivityType): boolean {
    try {
      if (this.cnfJson.showOnlySaveButton) {
        return false;
      }

      let hasConfirm = false;
      hasConfirm = hasConfirm || [EnActivityType.ProcessActivity].includes(activityType);
      hasConfirm = hasConfirm || this.isListDetail();
      hasConfirm = hasConfirm && this.atividade.ShowSave;

      return hasConfirm;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'hasConfirmButton', error.message);
    }

    return false;
  }

  ngOnChanges() {
    try {
      if (this.saved) {
        if (this.canShowSavedMessages)
          this.showSaveMessage(true);

        this.afterSaved();
        this.saved = false;
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'ngOnChanges', error.message);
    }
  }

  /**
   * Exibe uma mensagem, que desaparece automaticamente após um tempo.
   * Também previne que a mesma mensagem seja exibida novamente, ao mesmo tempo que a outra identica.
   */
  protected showMessage(msg: IMessage, interval: number = 1500): void {
    try {
      const prevMessage = this.msgs
        .filter(f => this.global.isEqual(f.detail, msg.detail) && this.global.isEqual(f.summary, msg.summary));

      if (!prevMessage || prevMessage.length <= 0) {
        this.msgs.push(msg);

        if (this.msgs && this.msgs.length > 0) {
          this.snackBar.open(
            this.msgs.map(m => m.detail).join(' '),
            this.msgs[0].summary,
            {
              duration: interval,
              verticalPosition: 'top'
            }
          );
        }
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'showMessage', error.message);
    }
  }

  /* Mensagem de tentativa de salvamento */
  protected showSaveMessage(isSuccess: boolean): void {
    try {
      if (this.canShowSavedMessages) {
        if (isSuccess) {
          this.showMessage({
            severity: EnMessageSeverity.success,
            summary: this.msg.ALERT_CONFIRMATION_TITLE,
            detail: this.msg.ALERT_ACTIVITY_SAVED
          });
        } else {
          this.showMessage({
            severity: EnMessageSeverity.error,
            summary: this.msg.ALERT_WARNING_TITLE,
            detail: this.msg.ALERT_ACTIVITY_SAVE_FAILED
          });
        }
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'showSaveMessage', error.message);
    }
  }

  /** Avisa o controle que a mensagem já foi exibida, para zerar o saved. */
  protected afterSaved(): void {
    try {
      this.eventBubble.emit({
        $event: null,
        bubbleEvent: EnBubbleEvent.afterSavedMessage
      } as IBubble);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'afterSaved', error.message);
    }
  }

  /* Identifica se a atividade atual é do tipo lista. */
  protected isListDetail(): boolean {
    try {
      return this.activityType === EnActivityType.ListDetail;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'hasSaveInList', error.message);
    }

    return false;
  }

  /* Resgata os valores inseridos no formGroup e armazena em um novo item.  */
  protected getValueFormGroup(newItem: any): any {
    try {
      if (!newItem) {
        newItem = {
          index: -1,
          label: ''
        };
      }

      const values = this.formGroup.getRawValue();
      for (const clm in values) {
        if (clm !== 'index') {
          if (this.lib.getVariavelNoFromId(clm) !== this.config.listIdVariableNo) {
            newItem[clm] = this.lib.getGEValue(values[clm]);
          }

          if (this.lib.getVariavelNoFromId(clm) === this.config.listTitleVariableNo) {
            newItem.label = values[clm];
          }
        }
      }

      return newItem;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'getValueFormGroup', error.message);
    }
  }

  /**
   * Botão de confirmação, que fará o historyBack, e salvará os dados apenas se o controle estiver configurado
   * para salvar os dados em Cadastro.
   * Na verdade, a AtividadeCadastro (list) está preparada para alterar o objeto gridItem, na memória,
   * que será utilizado para devolução dos dados à atividade principal, que chamou essa atividade.
   */
  onConfirm($event: any, saveInList: any): void {
    try {
      this.isLoading = true;

      const backAfterComplete = this.activityType !== EnActivityType.ListDetail;
      this.eventBubble.emit({
        $event,
        bubbleEvent: EnBubbleEvent.activityComplete,
        params: { backAfterComplete }
      } as IBubble);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onConfirm', error.message);
    }
  }

  /**
   * Além de salvar, cria um novo registro, com os dados em branco.
   * Nessa opção, é obrigatório a marcação de SaveInList, pois, caso contrário,
   * os dados não seriam salvos (na confirmação simples eles somente são transportados para
   * a tela anterior, onde devem ser salvos).
   */
  onSaveAndNew($event: any): void {
    try {
      this.isLoading = true;

      const ano = this.componentes[0].AtividadeNo;
      this.nav.preventAddToHistory = true;

      this.eventBubble.emit({
        $event,
        bubbleEvent: EnBubbleEvent.listSaveAndNew,
        params: { ano, saveInList: true, addToHistory: this.addToHistory }
      } as IBubble);

      this.resetTab();
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onSaveAndNew', error.message);
    }
  }

  /**
   * Força a tab para o primeiro item.
   * Se isso não for feito, a primeira aba poder não estar selecionada,
   * e a tela fica que branco.
   */
  protected resetTab(): void {
    try {
      this.eventBubble.emit({
        bubbleEvent: EnBubbleEvent.tabChange,
        params: { tabIndex: 0 }
      } as IBubble);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'resetTab', error.message);
    }
  }

  /* Similar ao Salvar e novo, no entanto, copia os dados da atividade atual para a próxima, exceto index. */
  onDuplicate($event: any): void {
    try {
      if (!this.isDuplicateAllowedForProcess()) {
        return;
      }
      this.isLoading = true;

      const dialogRef = this.matDialog.open(DialogConfirmComponent, {
        maxWidth: '560px',
        minWidth: '360px',
        data: {
          title: 'Confirmar Duplicação',
          text: 'Uma nova Evolução será criada a partir desta.\nTodos os dados serão copiados para a nova Evolução.\nATENÇÃO: lembre-se de atualizar a Data da Evolução e todos os demais campos que necessitam de alteração.\n\nDeseja continuar?',
          confirmLabel: 'Confirmar duplicação'
        }
      });

      this.subs.sink = dialogRef.afterClosed().subscribe(result => {
        if (result?.wasConfirmed) {
          this.executeDuplicate($event);
        } else {
          this.isLoading = false;
        }
      });
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onDuplicate', error.message);
    }
  }

  /** Executa a duplicação da ocorrência após confirmação (ou diretamente quando sem confirmação). */
  private executeDuplicate($event: any): void {
    try {
      // New
      const ano = this.componentes[0].AtividadeNo;
      const newItem = this.getValueFormGroup(null);

      // Sobrescreve com a data atual os campos que não devem herdar a data da ocorrência original
      this.variaveisDataReset.forEach(vno => newItem[this.lib.getId(vno)] = this.global.DateToddMMYYYY(new Date()));

      this.config.setDefaultFormControls(ano, newItem);
      this.nav.preventAddToHistory = true;

      this.eventBubble.emit({
        $event,
        bubbleEvent: EnBubbleEvent.listDuplicate,
        params: { ano, saveInList: true, addToHistory: this.addToHistory }
      } as IBubble);

      this.resetTab();
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'executeDuplicate', error.message);
    }
  }

  /**
   * Exibe mensagem de confirmação e, se confirmado, exclui o item do Cadastro permanentemente.
   */
  onDelete($event: any): void {
    try {
      const message = {
        firstButtonLabel: 'Não',
        title: 'Confirmação',
        icon: 'fa-times',
        text: `Você tem certeza que quer excluir esse item? Essa exclusão NÃO poderá ser desfeita!`,
        acceptFunc: ({ wasConfirmed }) => {
          try {
            if (!wasConfirmed) {
              return;
            }

            this.eventBubble.emit({
              $event,
              bubbleEvent: EnBubbleEvent.listDelete,
              params: {
                ano: this.config.ModeloAtividadeNo,
                ono: this.config.OcorrenciaNo.value,
                pno: this.atividade.ProcessoNo
              }
            } as IBubble);

            this.eventBubble.emit({
              $event,
              bubbleEvent: EnBubbleEvent.gotoGenericList
            } as IBubble);
          } catch (error) {
            this.log.Registrar(this.constructor.name, 'onDelete.acceptFunc', error.message);
          }
        }
      };

      const size = { height: '200px', width: '800px' };

      this.onAlertDialog($event, message, true, size);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onDelete', error.message);
    }
  }

  /**
   * Emite um evento para a Atividade View para exibição de uma janela de Alerta.
   */
  protected onAlertDialog($event: any, message: any, hasConfirmButton: boolean, size: { height: string, width: string }): void {
    try {
      this.eventBubble.emit({
        $event,
        bubbleEvent: EnBubbleEvent.alertDialog,
        params: { message, hasConfirmButton, size }
      } as IBubble);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onAlertDialog', error.message);
    }
  }

  /* Clique no botão anexos. */
  onAttachment($event: any): void {
    try {
      const url = this.cnfJson.attachmentUrl(
        this.config.OcorrenciaNo.value,
        this.config.ModeloAtividadeNo,
        this.config.usuarioLogadoNo,
        '60vh'
      );

      if (this.attachmentCount >= 0) {
        this.eventBubble.emit({
          $event,
          bubbleEvent: EnBubbleEvent.openAttachments,
          params: {
            url,
            ono: this.config.OcorrenciaNo.value,
            ano: this.config.ModeloAtividadeNo,
            tno: this.config.TarefaNo,
            width: '100%',
            height: '100vh'
          }
        } as IBubble);
      }
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'onAttachment', error.message);
    }
  }

  /**
   * Verifica se o dark mode deve ser aplicado.
   */
  isDarkMode(): boolean {
    try {
      return this.enTheme === EnTheme.black;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'isBlack', error.message);
    }

    return false;
  }
}

